import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import MetricsCard from '../dashboard/components/MetricsCard';
import { supabase } from '../../utils/supabase';

const DeveloperDashboard = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalAgents: 0,
    activeAgents: 0,
    totalContracts: 0,
    contractsThisMonth: 0,
    averageProcessingTime: 0,
    topAgent: null
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user && userProfile?.role !== 'developer' && userProfile?.role !== 'admin') {
      navigate('/contract-upload');
    }
  }, [user, userProfile, navigate]);

  useEffect(() => {
    if (user && (userProfile?.role === 'developer' || userProfile?.role === 'admin')) {
      loadDeveloperStats();
      loadRecentActivity();
    }
  }, [user, userProfile]);

  const loadDeveloperStats = async () => {
    try {
      // Get total agents
      const { data: agents, error: agentsError } = await supabase
        .from('user_profiles')
        .select('id, last_login_at, subscription_status')
        .eq('role', 'agent');

      if (agentsError) {
        console.log('Error loading agents:', agentsError);
        return;
      }

      // Get total contracts
      const { data: contracts, error: contractsError } = await supabase
        .from('contracts')
        .select('id, created_at, status, agent_id');

      if (contractsError) {
        console.log('Error loading contracts:', contractsError);
        return;
      }

      // Calculate stats
      const now = new Date();
      const thirtyDaysAgo = new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000));
      
      const activeAgents = agents?.filter(agent => 
        agent.last_login_at && new Date(agent.last_login_at) > thirtyDaysAgo
      ).length || 0;

      const contractsThisMonth = contracts?.filter(contract => 
        new Date(contract.created_at) > thirtyDaysAgo
      ).length || 0;

      // Find top agent by contract count
      const agentContractCounts = {};
      contracts?.forEach(contract => {
        if (contract.agent_id) {
          agentContractCounts[contract.agent_id] = (agentContractCounts[contract.agent_id] || 0) + 1;
        }
      });

      const topAgentId = Object.keys(agentContractCounts).reduce((a, b) => 
        agentContractCounts[a] > agentContractCounts[b] ? a : b, null
      );

      let topAgent = null;
      if (topAgentId) {
        const { data: topAgentData } = await supabase
          .from('user_profiles')
          .select('full_name, company_name')
          .eq('id', topAgentId)
          .single();
        
        topAgent = topAgentData;
      }

      setStats({
        totalAgents: agents?.length || 0,
        activeAgents,
        totalContracts: contracts?.length || 0,
        contractsThisMonth,
        averageProcessingTime: 4.2, // Mock average
        topAgent
      });
    } catch (error) {
      console.log('Error loading developer stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRecentActivity = async () => {
    try {
      const { data: activity, error } = await supabase
        .from('platform_usage')
        .select(`
          id,
          action,
          metadata,
          created_at,
          user_profiles!platform_usage_agent_id_fkey (
            full_name,
            company_name
          )
        `)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) {
        console.log('Error loading recent activity:', error);
        return;
      }

      setRecentActivity(activity || []);
    } catch (error) {
      console.log('Error loading recent activity:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-20">
          <div className="max-w-7xl mx-auto px-6 py-8">
            <div className="animate-pulse">
              <div className="h-8 bg-muted rounded w-64 mb-4"></div>
              <div className="h-4 bg-muted rounded w-96 mb-8"></div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-32 bg-muted rounded-lg"></div>
                ))}
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const metricsData = [
    {
      title: "Total Agents",
      value: stats.totalAgents.toString(),
      subtitle: `${stats.activeAgents} active this month`,
      icon: "Users",
      color: "primary",
      trend: "up",
      trendValue: "+12%"
    },
    {
      title: "Contracts Processed",
      value: stats.totalContracts.toString(),
      subtitle: `${stats.contractsThisMonth} this month`,
      icon: "FileText",
      color: "success",
      trend: "up",
      trendValue: "+8%"
    },
    {
      title: "Avg. Processing Time",
      value: `${stats.averageProcessingTime}min`,
      subtitle: "Per contract",
      icon: "Clock",
      color: "warning",
      trend: "down",
      trendValue: "-15%"
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-20">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Page Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
                Developer Dashboard
              </h1>
              <p className="text-muted-foreground font-body">
                Monitor platform usage, agent activity, and system performance
              </p>
            </div>
            
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                iconName="Download"
                iconPosition="left"
                iconSize={18}
              >
                Export Data
              </Button>
              <Button
                variant="default"
                iconName="Settings"
                iconPosition="left"
                iconSize={18}
              >
                Platform Settings
              </Button>
            </div>
          </div>

          {/* Platform Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {metricsData.map((metric, index) => (
              <MetricsCard
                key={index}
                title={metric.title}
                value={metric.value}
                subtitle={metric.subtitle}
                icon={metric.icon}
                color={metric.color}
                trend={metric.trend}
                trendValue={metric.trendValue}
              />
            ))}
          </div>

          {/* Content Grid */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
            {/* Recent Activity */}
            <div className="xl:col-span-2">
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-heading font-semibold text-foreground">
                    Recent Platform Activity
                  </h2>
                  <Button variant="ghost" size="sm" iconName="RefreshCw" iconSize={16}>
                    Refresh
                  </Button>
                </div>

                <div className="space-y-4">
                  {recentActivity.map((activity, index) => (
                    <div key={activity.id} className="flex items-center space-x-4 p-4 bg-muted/50 rounded-lg">
                      <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                        <span className="text-primary font-semibold text-sm">
                          {activity.user_profiles?.full_name?.[0] || 'U'}
                        </span>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <span className="font-medium text-foreground">
                            {activity.user_profiles?.full_name || 'Unknown User'}
                          </span>
                          <span className="text-muted-foreground text-sm">
                            {activity.action.replace('_', ' ')}
                          </span>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {activity.user_profiles?.company_name && (
                            <span>{activity.user_profiles.company_name} • </span>
                          )}
                          {new Date(activity.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {recentActivity.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      No recent activity to display
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* System Status & Top Agent */}
            <div className="space-y-6">
              {/* System Status */}
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-lg font-heading font-semibold text-foreground mb-4">
                  System Status
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Database</span>
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-success rounded-full"></div>
                      <span className="text-success text-sm font-medium">Online</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Authentication</span>
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-success rounded-full"></div>
                      <span className="text-success text-sm font-medium">Online</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">File Storage</span>
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-success rounded-full"></div>
                      <span className="text-success text-sm font-medium">Online</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Top Agent */}
              {stats.topAgent && (
                <div className="bg-card border border-border rounded-lg p-6">
                  <h3 className="text-lg font-heading font-semibold text-foreground mb-4">
                    Top Agent This Month
                  </h3>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                      <span className="text-primary font-semibold text-xl">
                        {stats.topAgent.full_name?.[0] || 'T'}
                      </span>
                    </div>
                    <h4 className="font-medium text-foreground mb-1">
                      {stats.topAgent.full_name}
                    </h4>
                    {stats.topAgent.company_name && (
                      <p className="text-sm text-muted-foreground mb-3">
                        {stats.topAgent.company_name}
                      </p>
                    )}
                    <div className="bg-primary/10 rounded-lg p-3">
                      <p className="text-primary font-semibold">
                        Most contracts processed
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default DeveloperDashboard;